import React, { useContext, createContext } from 'react';
import { ChevronDown } from 'lucide-react';

// Context to share accordion state
const AccordionContext = createContext({
  activeKey: null,
  onSelect: () => {},
});

// Context to pass eventKey from Item to Header/Body
const ItemContext = createContext({ eventKey: undefined });

/**
 * Tailwind-based Accordion component
 * Drop-in replacement for react-bootstrap Accordion
 * 
 * Usage:
 * <Accordion activeKey={activeKey} onSelect={setActiveKey}>
 *   <Accordion.Item eventKey="0">
 *     <Accordion.Header>Title</Accordion.Header>
 *     <Accordion.Body>Content</Accordion.Body>
 *   </Accordion.Item>
 * </Accordion>
 */
const Accordion = ({ activeKey, onSelect, className = '', children, ...props }) => {
  const handleSelect = (eventKey) => {
    // Toggle: if clicking the same key, close it (set to null)
    const newKey = activeKey === eventKey ? null : eventKey;
    if (onSelect) {
      onSelect(newKey);
    }
  };

  return (
    <AccordionContext.Provider value={{ activeKey, onSelect: handleSelect }}>
      <div className={className} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

const Item = ({ eventKey, className = '', children, id, ...props }) => {
  const { activeKey } = useContext(AccordionContext);
  const isActive = activeKey === eventKey;

  // Always show horizontal border between items (thin light gray)
  // Allow custom classes to override if needed
  const hasCustomBorder = className.includes('border-b') || className.includes('border-0');
  const defaultClasses = hasCustomBorder ? '' : 'border-b border-gray-100';

  return (
    <div
      id={id}
      className={`${defaultClasses} ${className}`}
      {...props}
    >
      <ItemContext.Provider value={{ eventKey }}>
        {children}
      </ItemContext.Provider>
    </div>
  );
};

const Header = ({ className = '', children, onClick, ...props }) => {
  const { activeKey, onSelect } = useContext(AccordionContext);
  const { eventKey } = useContext(ItemContext);
  const isActive = activeKey === eventKey;

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
    if (eventKey !== undefined && onSelect) {
      onSelect(eventKey);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isActive}
      aria-controls={eventKey !== undefined ? `accordion-body-${eventKey}` : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        flex items-center justify-between
        px-4 py-3
        ${isActive ? 'bg-blue-50 shadow-sm' : 'bg-white'}
        cursor-pointer
        transition-all duration-150
        select-none
        ${isActive ? 'rounded-t' : ''}
        ${className}
      `}
      {...props}
    >
      <div className={`flex-1 ${isActive ? 'text-blue-900' : 'text-gray-700'}`}>{children}</div>
      <ChevronDown
        className={`
          h-5 w-5
          ${isActive ? 'text-blue-900' : 'text-gray-500'}
          transition-all duration-300 ease-in-out
          flex-shrink-0 ml-2
          ${isActive ? 'transform rotate-180' : ''}
        `}
      />
    </div>
  );
};

const Body = ({ className = '', children, ...props }) => {
  const { activeKey } = useContext(AccordionContext);
  const { eventKey } = useContext(ItemContext);
  const isActive = activeKey === eventKey;

  return (
    <div
      id={eventKey !== undefined ? `accordion-body-${eventKey}` : undefined}
      className={`
        grid transition-[grid-template-rows] duration-200 ease-out
        ${isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
      `}
    >
      <div className="overflow-hidden">
        <div className={`px-4 py-3 bg-white ${className}`} {...props}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Attach sub-components
Accordion.Item = Item;
Accordion.Header = Header;
Accordion.Body = Body;

export default Accordion;
